import {
  Button,
  FieldWrapper,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import {
  ApiCallHttpMethod,
  TMapping,
  TSysApiCallStepDefinition,
  TValueProvider,
} from '@commenda/types';
import { useState } from 'react';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { MappingModal } from '../modals/AddEditMappingModal';
import { VariablesBindingModal } from '../modals/VariablesBindingModal';

const TEMPLATE_VAR_REGEX = /\{\{([^}]+)\}\}/g;

function extractUrlVariables(url: string | undefined | null): string[] {
  if (!url || typeof url !== 'string') return [];
  const matches = Array.from(url.matchAll(TEMPLATE_VAR_REGEX));
  return Array.from(new Set(matches.map((m) => m[1].trim())));
}

export const ApiCallNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();

  const addEditBodyMappingModalVisibility = useToggle(false);
  const addEditContextProjectionModalVisibility = useToggle(false);
  const urlBindingModalVisibility = useToggle(false);
  const bodyBindingModalVisibility = useToggle(false);

  const [mappingToEdit, setMappingToEdit] = useState<TMapping | null>(null);
  const [mappingType, setMappingType] = useState<
    'body' | 'contextProjection'
  >('body');
  const [editingUrlVar, setEditingUrlVar] = useState<string | null>(null);
  const [editingBodyVar, setEditingBodyVar] = useState<string | null>(null);

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as TSysApiCallStepDefinition;

  const updateApiCallStep = (updates: Partial<TSysApiCallStepDefinition>) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, ...updates } : step
      ),
    });
  };

  const onSaveBodyMapping = (mapping: TMapping) => {
    const currentBody = selectedStep.body;
    if (!currentBody || currentBody.type !== 'json') {
      updateApiCallStep({
        body: { type: 'json', content: [mapping] },
      });
      return;
    }

    const existingMapping = currentBody.content?.find(
      (m) => m.id === mapping.id
    );
    if (existingMapping) {
      updateApiCallStep({
        body: {
          ...currentBody,
          content: currentBody.content.map((m) =>
            m.id === mapping.id ? mapping : m
          ),
        },
      });
    } else {
      updateApiCallStep({
        body: {
          ...currentBody,
          content: [...(currentBody.content || []), mapping],
        },
      });
    }
  };

  const onSaveContextProjection = (mapping: TMapping) => {
    const existingMapping = selectedStep.contextProjection?.find(
      (m) => m.id === mapping.id
    );
    if (existingMapping) {
      updateApiCallStep({
        contextProjection: selectedStep.contextProjection?.map((m) =>
          m.id === mapping.id ? mapping : m
        ),
      });
    } else {
      updateApiCallStep({
        contextProjection: [...(selectedStep.contextProjection || []), mapping],
      });
    }
  };

  if (!selectedStep) return <div>Step not found</div>;

  const urlValue = selectedStep.url || '';

  // URL template variable handling
  const handleUrlChange = (newUrl: string) => {
    const variables = extractUrlVariables(newUrl);
    const existing = selectedStep.urlValueMapping ?? {};
    const merged: Record<string, TValueProvider> = {};
    variables.forEach((v) => {
      if (existing[v]) merged[v] = existing[v];
    });

    updateApiCallStep({
      url: newUrl,
      urlValueMapping: Object.keys(merged).length > 0 ? merged : undefined,
    });
  };

  const openUrlBindingModal = (varName: string) => {
    setEditingUrlVar(varName);
    urlBindingModalVisibility.show();
  };

  const onSaveUrlBinding = (varName: string, value: TValueProvider) => {
    updateApiCallStep({
      urlValueMapping: {
        ...(selectedStep.urlValueMapping ?? {}),
        [varName]: value,
      },
    });
  };

  const urlVariables = extractUrlVariables(urlValue);
  const urlValueMapping = selectedStep.urlValueMapping ?? {};
  const missingUrlBindings = urlVariables.filter((v) => !urlValueMapping[v]);

  // Body template variable handling
  const rawBodyValue =
    selectedStep.body?.type === 'raw'
      ? typeof (selectedStep.body as any)?.content?.value === 'string'
        ? (selectedStep.body as any)?.content?.value
        : JSON.stringify(
            (selectedStep.body as any)?.content?.value ?? '',
            null,
            2
          )
      : '';

  const handleBodyChange = (newBody: string) => {
    const variables = extractUrlVariables(newBody);
    const existing = selectedStep.bodyValueMapping ?? {};
    const merged: Record<string, TValueProvider> = {};
    variables.forEach((v) => {
      if (existing[v]) merged[v] = existing[v];
    });

    updateApiCallStep({
      body: {
        ...(selectedStep.body as any),
        type: 'raw',
        content: { type: 'const', value: newBody },
      },
      bodyValueMapping: Object.keys(merged).length > 0 ? merged : undefined,
    });
  };

  const openBodyBindingModal = (varName: string) => {
    setEditingBodyVar(varName);
    bodyBindingModalVisibility.show();
  };

  const onSaveBodyBinding = (varName: string, value: TValueProvider) => {
    updateApiCallStep({
      bodyValueMapping: {
        ...(selectedStep.bodyValueMapping ?? {}),
        [varName]: value,
      },
    });
  };

  const bodyVariables = extractUrlVariables(rawBodyValue);
  const bodyValueMapping = selectedStep.bodyValueMapping ?? {};
  const missingBodyBindings = bodyVariables.filter((v) => !bodyValueMapping[v]);

  return (
    <div className="flex flex-col gap-4">
      {/* HTTP Method */}
      <FieldWrapper>
        <Label>HTTP Method</Label>
        <Select
          value={selectedStep.method || ApiCallHttpMethod.GET}
          onValueChange={(value) => {
            updateApiCallStep({ method: value as ApiCallHttpMethod });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ApiCallHttpMethod.GET}>GET</SelectItem>
            <SelectItem value={ApiCallHttpMethod.POST}>POST</SelectItem>
            <SelectItem value={ApiCallHttpMethod.PUT}>PUT</SelectItem>
            <SelectItem value={ApiCallHttpMethod.PATCH}>PATCH</SelectItem>
            <SelectItem value={ApiCallHttpMethod.DELETE}>DELETE</SelectItem>
          </SelectContent>
        </Select>
      </FieldWrapper>

      {/* Path */}
      <FieldWrapper>
        <Label>Path</Label>
        <Input
          placeholder="/api/registrations/{{id}}"
          value={urlValue}
          onChange={(e) => handleUrlChange(e.target.value)}
        />
        <p className="txt-extra-small-regular text-text-secondary mt-1">
          Use {`{{varName}}`} to insert dynamic values into the path.
        </p>
      </FieldWrapper>

      {/* URL Variable Bindings */}
      {urlVariables.length > 0 && (
        <FieldWrapper>
          <Label>URL Variable Bindings</Label>
          <div className="flex flex-col gap-2">
            {urlVariables.map((v) => (
              <div key={v} className="flex items-center justify-between gap-2">
                <span className="txt-small-regular text-text-primary font-mono">
                  {`{{${v}}}`}
                </span>
                <div className="flex items-center gap-2">
                  {urlValueMapping[v] && (
                    <span className="txt-extra-small-regular text-text-secondary truncate max-w-[120px]">
                      {urlValueMapping[v].type === 'field'
                        ? `${(urlValueMapping[v] as any).ref?.source}.${(urlValueMapping[v] as any).ref?.path}`
                        : urlValueMapping[v].type === 'expr'
                          ? 'expression'
                          : 'const'}
                    </span>
                  )}
                  <Icons.Edit01
                    className="size-4 cursor-pointer"
                    onClick={() => openUrlBindingModal(v)}
                  />
                </div>
              </div>
            ))}
          </div>

          {missingUrlBindings.length > 0 && (
            <p className="txt-extra-small-regular text-text-error mt-1">
              Missing binding for: {missingUrlBindings.join(', ')}
            </p>
          )}
        </FieldWrapper>
      )}

      {/* Body Type */}
      <FieldWrapper>
        <Label>Request Body</Label>
        <Select
          value={selectedStep.body?.type || 'none'}
          onValueChange={(value) => {
            if (value === 'none') {
              updateApiCallStep({ body: { type: 'none' } });
            } else if (value === 'json') {
              updateApiCallStep({ body: { type: 'json', content: [] } });
            } else if (value === 'raw') {
              updateApiCallStep({
                body: {
                  type: 'raw',
                  content: { type: 'const', value: '' },
                  contentType: 'application/json',
                },
              });
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select body type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="json">JSON (via Mappings)</SelectItem>
            <SelectItem value="raw">Raw</SelectItem>
          </SelectContent>
        </Select>
      </FieldWrapper>

      {/* Body Mappings for JSON */}
      {selectedStep.body?.type === 'json' && (
        <FieldWrapper>
          <Label className="flex justify-between items-center">
            <span className="grow">Body Mappings</span>
            <Button
              size="sm"
              onClick={() => {
                setMappingToEdit(null);
                setMappingType('body');
                addEditBodyMappingModalVisibility.show();
              }}
            >
              <Icons.Plus className="size-4" />
            </Button>
          </Label>
          <div className="flex flex-col gap-2">
            {selectedStep.body.content?.map((mapping) => (
              <div
                key={mapping.id}
                className="flex items-center justify-between gap-2"
              >
                <span className="text-sm">{mapping.targetPath}</span>
                <div className="flex items-center gap-2">
                  <Icons.Edit01
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      setMappingToEdit(mapping);
                      setMappingType('body');
                      addEditBodyMappingModalVisibility.show();
                    }}
                  />
                  <Icons.Trash02
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      updateApiCallStep({
                        body: {
                          ...selectedStep.body,
                          type: 'json',
                          content: (
                            selectedStep.body as {
                              type: 'json';
                              content: TMapping[];
                            }
                          ).content.filter((m) => m.id !== mapping.id),
                        },
                      });
                    }}
                  />
                </div>
              </div>
            ))}
            {(!selectedStep.body.content ||
              selectedStep.body.content.length === 0) && (
              <div className="text-sm text-gray-500">No body mappings</div>
            )}
          </div>
        </FieldWrapper>
      )}

      {/* Raw Body */}
      {selectedStep.body?.type === 'raw' && (
        <>
          <FieldWrapper>
            <Label>Raw Body Content</Label>
            <Textarea
              placeholder='{"id": "{{post_id}}", "title": "{{title}}"}'
              value={
                typeof (selectedStep.body as any)?.content?.value === 'string'
                  ? (selectedStep.body as any)?.content?.value
                  : JSON.stringify(
                      (selectedStep.body as any)?.content?.value ?? '',
                      null,
                      2
                    )
              }
              onChange={(e) => handleBodyChange(e.target.value)}
              rows={4}
            />
            <p className="txt-extra-small-regular text-text-secondary mt-1">
              Use {`{{varName}}`} to insert dynamic values into the body.
            </p>
          </FieldWrapper>

          {/* Body Variable Bindings */}
          {bodyVariables.length > 0 && (
            <FieldWrapper>
              <Label>Body Variable Bindings</Label>
              <div className="flex flex-col gap-2">
                {bodyVariables.map((v) => (
                  <div
                    key={v}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="txt-small-regular text-text-primary font-mono">
                      {`{{${v}}}`}
                    </span>
                    <div className="flex items-center gap-2">
                      {bodyValueMapping[v] && (
                        <span className="txt-extra-small-regular text-text-secondary truncate max-w-[120px]">
                          {bodyValueMapping[v].type === 'field'
                            ? `${(bodyValueMapping[v] as any).ref?.source}.${(bodyValueMapping[v] as any).ref?.path}`
                            : bodyValueMapping[v].type === 'expr'
                              ? 'expression'
                              : 'const'}
                        </span>
                      )}
                      <Icons.Edit01
                        className="size-4 cursor-pointer"
                        onClick={() => openBodyBindingModal(v)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {missingBodyBindings.length > 0 && (
                <p className="txt-extra-small-regular text-text-error mt-1">
                  Missing binding for: {missingBodyBindings.join(', ')}
                </p>
              )}
            </FieldWrapper>
          )}
        </>
      )}

      {/* Context Projection */}
      <FieldWrapper>
        <Label className="flex justify-between items-center">
          <span className="grow">Context Projection</span>
          <Button
            size="sm"
            onClick={() => {
              setMappingToEdit(null);
              setMappingType('contextProjection');
              addEditContextProjectionModalVisibility.show();
            }}
          >
            <Icons.Plus className="size-4" />
          </Button>
        </Label>
        <div className="flex flex-col gap-2">
          {selectedStep.contextProjection?.map((mapping) => (
            <div
              key={mapping.id}
              className="flex items-center justify-between gap-2"
            >
              <span className="text-sm">{mapping.targetPath}</span>
              <div className="flex items-center gap-2">
                <Icons.Edit01
                  className="size-4 cursor-pointer"
                  onClick={() => {
                    setMappingToEdit(mapping);
                    setMappingType('contextProjection');
                    addEditContextProjectionModalVisibility.show();
                  }}
                />
                <Icons.Trash02
                  className="size-4 cursor-pointer"
                  onClick={() => {
                    updateApiCallStep({
                      contextProjection: selectedStep.contextProjection?.filter(
                        (m) => m.id !== mapping.id
                      ),
                    });
                  }}
                />
              </div>
            </div>
          ))}
          {(!selectedStep.contextProjection ||
            selectedStep.contextProjection.length === 0) && (
            <div className="text-sm text-gray-500">No context projection</div>
          )}
        </div>
      </FieldWrapper>

      {/* Error Handling */}
      <FieldWrapper>
        <Label>On Error</Label>
        <Select
          value={selectedStep.errorHandling?.onError || 'fail'}
          onValueChange={(value) => {
            updateApiCallStep({
              errorHandling: { onError: value as 'fail' | 'continue' },
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select error handling" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fail">Fail (Stop Workflow)</SelectItem>
            <SelectItem value="continue">Continue (With Error)</SelectItem>
          </SelectContent>
        </Select>
      </FieldWrapper>

      {/* Timeout */}
      <FieldWrapper>
        <Label>Timeout (ms)</Label>
        <Input
          type="number"
          placeholder="30000"
          value={selectedStep.timeoutMs || ''}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            updateApiCallStep({
              timeoutMs: isNaN(value) ? undefined : value,
            });
          }}
        />
      </FieldWrapper>

      {/* Modals */}
      <MappingModal
        isOpen={addEditBodyMappingModalVisibility.visible}
        onClose={() => {
          addEditBodyMappingModalVisibility.hide();
          setMappingToEdit(null);
        }}
        mapping={mappingToEdit}
        onMappingChange={onSaveBodyMapping}
        workflowDefinition={workflow}
        currentStepId={selectedEntity.id}
      />

      <MappingModal
        isOpen={addEditContextProjectionModalVisibility.visible}
        onClose={() => {
          addEditContextProjectionModalVisibility.hide();
          setMappingToEdit(null);
        }}
        mapping={mappingToEdit}
        onMappingChange={onSaveContextProjection}
        workflowDefinition={workflow}
        currentStepId={selectedEntity.id}
      />

      <VariablesBindingModal
        isOpen={urlBindingModalVisibility.visible}
        onClose={() => {
          urlBindingModalVisibility.hide();
          setEditingUrlVar(null);
        }}
        variableName={editingUrlVar}
        value={editingUrlVar ? (urlValueMapping[editingUrlVar] ?? null) : null}
        onSave={onSaveUrlBinding}
        workflowDefinition={workflow}
        currentStepId={selectedEntity.id}
      />

      <VariablesBindingModal
        isOpen={bodyBindingModalVisibility.visible}
        onClose={() => {
          bodyBindingModalVisibility.hide();
          setEditingBodyVar(null);
        }}
        variableName={editingBodyVar}
        value={
          editingBodyVar ? (bodyValueMapping[editingBodyVar] ?? null) : null
        }
        onSave={onSaveBodyBinding}
        workflowDefinition={workflow}
        currentStepId={selectedEntity.id}
      />
    </div>
  );
};
